// src/errors/XoneUiError.ts
var XoneUiError = class _XoneUiError extends Error {
  code;
  details;
  constructor(code, message, details) {
    super(message);
    this.name = "XoneUiError";
    this.code = code;
    this.details = details;
    Object.setPrototypeOf(this, _XoneUiError.prototype);
  }
};
var XoneUiCanceledError = class _XoneUiCanceledError extends XoneUiError {
  constructor(message = "Operacion cancelada por el usuario.") {
    super("XONE.UI.CANCELED", message);
    this.name = "XoneUiCanceledError";
    Object.setPrototypeOf(this, _XoneUiCanceledError.prototype);
  }
};

// src/webcomponents/registerWebComponents.ts
var TAGS = [
  "xone-msgbox",
  "xone-confirm",
  "xone-prompt",
  "xone-toast",
  "xone-snackbar",
  "xone-wait",
  "xone-datepicker",
  "xone-timepicker",
  "xone-signature",
  "xone-camera"
];
function xoneColorToCss(color) {
  if (!color.startsWith("#")) return color;
  if (color.length === 9) {
    const alpha = color.slice(1, 3);
    const rgb = color.slice(3);
    return `#${rgb}${alpha}`;
  }
  return color;
}
var XoneDialogElement = class extends HTMLElement {
  _resolve = null;
  _reject = null;
  _shadow;
  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: "open" });
  }
  bindPromise(resolve, reject) {
    this._resolve = resolve;
    this._reject = reject;
  }
  close(result) {
    if (this._resolve) {
      this._resolve(result);
      this._resolve = null;
      this._reject = null;
    }
    this.dispatchEvent(
      new CustomEvent("xone-result", {
        detail: { result },
        bubbles: true
      })
    );
    queueMicrotask(() => {
      if (this.parentNode) this.parentNode.removeChild(this);
    });
  }
};
var XoneMsgBoxElement = class extends XoneDialogElement {
  connectedCallback() {
    const message = this.getAttribute("message") ?? "";
    const title = this.getAttribute("title") ?? "";
    const icon = this.getAttribute("icon") ?? "info";
    const buttons = Number(this.getAttribute("buttons") ?? "0");
    this._shadow.innerHTML = this.template(title, message, icon, buttons);
    const buttonsContainer = this._shadow.querySelector(".xone-buttons");
    buttonsContainer?.addEventListener("click", (ev) => {
      const target = ev.target;
      const code = target.getAttribute("data-result");
      if (code) this.close(Number(code));
    });
  }
  template(title, message, icon, buttons) {
    const btns = this.buttonsFor(buttons);
    return `
            <style>${baseModalCss()}</style>
            <div class="xone-overlay" part="overlay">
                <div class="xone-card" role="alertdialog" aria-modal="true">
                    ${title ? `<header class="xone-title" data-icon="${icon}">${escapeHtml(title)}</header>` : ""}
                    <section class="xone-body">${escapeHtml(message)}</section>
                    <footer class="xone-buttons">${btns}</footer>
                </div>
            </div>`;
  }
  buttonsFor(buttons) {
    const make = (label, code, primary = false) => `<button type="button" data-result="${code}" class="${primary ? "primary" : ""}">${label}</button>`;
    switch (buttons) {
      case 1:
        return make("Cancelar", 2) + make("Aceptar", 1, true);
      case 2:
        return make("Anular", 3) + make("Reintentar", 4) + make("Ignorar", 5, true);
      case 3:
        return make("Cancelar", 2) + make("No", 7) + make("Si", 6, true);
      case 4:
        return make("No", 7) + make("Si", 6, true);
      case 5:
        return make("Cancelar", 2) + make("Reintentar", 4, true);
      case 0:
      default:
        return make("Aceptar", 1, true);
    }
  }
};
var XonePromptElement = class extends XoneDialogElement {
  connectedCallback() {
    const message = this.getAttribute("message") ?? "";
    const title = this.getAttribute("title") ?? "";
    const defaultValue = this.getAttribute("default") ?? "";
    this._shadow.innerHTML = `
            <style>${baseModalCss()}</style>
            <div class="xone-overlay">
                <div class="xone-card" role="dialog" aria-modal="true">
                    ${title ? `<header class="xone-title">${escapeHtml(title)}</header>` : ""}
                    <section class="xone-body">
                        <p>${escapeHtml(message)}</p>
                        <input type="text" class="xone-input" value="${escapeHtml(defaultValue)}" />
                    </section>
                    <footer class="xone-buttons">
                        <button type="button" data-action="cancel">Cancelar</button>
                        <button type="button" data-action="ok" class="primary">Aceptar</button>
                    </footer>
                </div>
            </div>`;
    const input = this._shadow.querySelector(".xone-input");
    input?.focus();
    this._shadow.querySelector(".xone-buttons")?.addEventListener("click", (ev) => {
      const t = ev.target;
      const action = t.getAttribute("data-action");
      if (action === "ok") this.close(input?.value ?? "");
      if (action === "cancel") this.close(null);
    });
    input?.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") this.close(input.value);
      if (ev.key === "Escape") this.close(null);
    });
  }
};
var XoneToastElement = class extends HTMLElement {
  _shadow;
  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: "open" });
  }
  connectedCallback() {
    const message = this.getAttribute("message") ?? "";
    const duration = Number(this.getAttribute("duration") ?? "3000");
    this._shadow.innerHTML = `
            <style>${toastCss()}</style>
            <div class="xone-toast" role="status" aria-live="polite">${escapeHtml(message)}</div>`;
    setTimeout(() => {
      if (this.parentNode) this.parentNode.removeChild(this);
    }, duration);
  }
};
var XoneSnackbarElement = class extends HTMLElement {
  _shadow;
  _timer = null;
  actionCallback = null;
  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: "open" });
  }
  connectedCallback() {
    const message = this.getAttribute("message") ?? "";
    const actionLabel = this.getAttribute("action-label");
    const duration = Number(this.getAttribute("duration") ?? "4000");
    const actionHtml = actionLabel ? `<button type="button" class="action">${escapeHtml(actionLabel)}</button>` : "";
    this._shadow.innerHTML = `
            <style>${snackbarCss()}</style>
            <div class="xone-snackbar" role="status" aria-live="polite">
                <span class="msg">${escapeHtml(message)}</span>
                ${actionHtml}
            </div>`;
    this._shadow.querySelector(".action")?.addEventListener("click", () => {
      if (this.actionCallback) this.actionCallback();
      this.dismiss();
    });
    this._timer = setTimeout(() => this.dismiss(), duration);
  }
  dismiss() {
    if (this._timer) clearTimeout(this._timer);
    if (this.parentNode) this.parentNode.removeChild(this);
  }
};
var XoneWaitElement = class extends HTMLElement {
  _shadow;
  _maxValue = 0;
  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: "open" });
  }
  connectedCallback() {
    const message = this.getAttribute("message") ?? "Procesando...";
    this._maxValue = Number(this.getAttribute("max-value") ?? "0");
    this._shadow.innerHTML = `
            <style>${baseModalCss()}${waitCss()}</style>
            <div class="xone-overlay">
                <div class="xone-card xone-wait" role="status" aria-live="polite">
                    <div class="spinner" aria-hidden="true"></div>
                    <p class="msg">${escapeHtml(message)}</p>
                    <progress class="bar" value="0" max="${this._maxValue || 1}"
                              ${this._maxValue === 0 ? "hidden" : ""}></progress>
                </div>
            </div>`;
  }
  setMessage(msg) {
    const el = this._shadow.querySelector(".msg");
    if (el) el.textContent = msg;
  }
  setMaxValue(max) {
    this._maxValue = max;
    const bar = this._shadow.querySelector("progress");
    if (bar) {
      bar.max = max || 1;
      bar.hidden = !max;
    }
  }
  update(value, msg) {
    const bar = this._shadow.querySelector("progress");
    if (bar) bar.value = value;
    if (msg) this.setMessage(msg);
  }
};
var XoneDatePickerElement = class extends XoneDialogElement {
  connectedCallback() {
    const initial = this.getAttribute("initial");
    const dateStr = initial ? new Date(initial).toISOString().slice(0, 10) : "";
    this._shadow.innerHTML = `
            <style>${baseModalCss()}</style>
            <div class="xone-overlay">
                <div class="xone-card" role="dialog" aria-modal="true">
                    <header class="xone-title">Seleccionar fecha</header>
                    <section class="xone-body">
                        <input type="date" class="xone-input" value="${dateStr}" />
                    </section>
                    <footer class="xone-buttons">
                        <button type="button" data-action="cancel">Cancelar</button>
                        <button type="button" data-action="ok" class="primary">Aceptar</button>
                    </footer>
                </div>
            </div>`;
    const input = this._shadow.querySelector(".xone-input");
    this._shadow.querySelector(".xone-buttons")?.addEventListener("click", (ev) => {
      const t = ev.target;
      const a = t.getAttribute("data-action");
      if (a === "ok") this.close(input?.value ? new Date(input.value) : null);
      if (a === "cancel") this.close(null);
    });
  }
};
var XoneTimePickerElement = class extends XoneDialogElement {
  connectedCallback() {
    const initial = this.getAttribute("initial");
    let timeStr = "";
    if (initial) {
      const d = new Date(initial);
      timeStr = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    this._shadow.innerHTML = `
            <style>${baseModalCss()}</style>
            <div class="xone-overlay">
                <div class="xone-card" role="dialog" aria-modal="true">
                    <header class="xone-title">Seleccionar hora</header>
                    <section class="xone-body">
                        <input type="time" class="xone-input" value="${timeStr}" />
                    </section>
                    <footer class="xone-buttons">
                        <button type="button" data-action="cancel">Cancelar</button>
                        <button type="button" data-action="ok" class="primary">Aceptar</button>
                    </footer>
                </div>
            </div>`;
    const input = this._shadow.querySelector(".xone-input");
    this._shadow.querySelector(".xone-buttons")?.addEventListener("click", (ev) => {
      const t = ev.target;
      const a = t.getAttribute("data-action");
      if (a === "ok") {
        const v = input?.value;
        if (!v) return this.close(null);
        const [hh, mm] = v.split(":");
        const d = /* @__PURE__ */ new Date();
        d.setHours(Number(hh ?? "0"), Number(mm ?? "0"), 0, 0);
        this.close(d);
      }
      if (a === "cancel") this.close(null);
    });
  }
};
var XoneSignatureElement = class extends XoneDialogElement {
  canvas = null;
  connectedCallback() {
    const w = Number(this.getAttribute("width") ?? "400");
    const h = Number(this.getAttribute("height") ?? "200");
    const penColor = xoneColorToCss(this.getAttribute("pen-color") ?? "#FF000000");
    const bg = xoneColorToCss(this.getAttribute("bg-color") ?? "#FFFFFFFF");
    const title = this.getAttribute("title") ?? "Firma";
    this._shadow.innerHTML = `
            <style>${baseModalCss()}${signatureCss()}</style>
            <div class="xone-overlay">
                <div class="xone-card" role="dialog" aria-modal="true">
                    <header class="xone-title">${escapeHtml(title)}</header>
                    <section class="xone-body">
                        <canvas class="sig-canvas" width="${w}" height="${h}"
                                style="background:${bg};border:1px solid #cccccc"></canvas>
                    </section>
                    <footer class="xone-buttons">
                        <button type="button" data-action="clear">Limpiar</button>
                        <button type="button" data-action="cancel">Cancelar</button>
                        <button type="button" data-action="ok" class="primary">Aceptar</button>
                    </footer>
                </div>
            </div>`;
    this.canvas = this._shadow.querySelector("canvas");
    if (this.canvas) attachSignaturePad(this.canvas, penColor);
    this._shadow.querySelector(".xone-buttons")?.addEventListener("click", (ev) => {
      const t = ev.target;
      const a = t.getAttribute("data-action");
      if (a === "cancel") return this.close(null);
      if (a === "ok") return this.close(this.canvas);
      if (a === "clear" && this.canvas) {
        const ctx = this.canvas.getContext("2d");
        ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
    });
  }
};
function attachSignaturePad(canvas, penColor) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.strokeStyle = penColor;
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  let drawing = false;
  let lastX = 0;
  let lastY = 0;
  const start = (ev) => {
    drawing = true;
    const r = canvas.getBoundingClientRect();
    lastX = ev.clientX - r.left;
    lastY = ev.clientY - r.top;
  };
  const move = (ev) => {
    if (!drawing) return;
    const r = canvas.getBoundingClientRect();
    const x = ev.clientX - r.left;
    const y = ev.clientY - r.top;
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastX = x;
    lastY = y;
  };
  const end = () => {
    drawing = false;
  };
  canvas.addEventListener("pointerdown", start);
  canvas.addEventListener("pointermove", move);
  canvas.addEventListener("pointerup", end);
  canvas.addEventListener("pointerleave", end);
}
var XoneCameraElement = class extends XoneDialogElement {
  _stream = null;
  canvas = null;
  connectedCallback() {
    const w = Number(this.getAttribute("width") ?? "640");
    const h = Number(this.getAttribute("height") ?? "480");
    const facing = this.getAttribute("facing") ?? "environment";
    this._shadow.innerHTML = `
            <style>${baseModalCss()}${cameraCss()}</style>
            <div class="xone-overlay">
                <div class="xone-card" role="dialog" aria-modal="true">
                    <header class="xone-title">Camara</header>
                    <section class="xone-body">
                        <video class="cam-video" width="${w}" height="${h}" autoplay playsinline muted></video>
                        <canvas class="cam-canvas" width="${w}" height="${h}" hidden></canvas>
                    </section>
                    <footer class="xone-buttons">
                        <button type="button" data-action="cancel">Cancelar</button>
                        <button type="button" data-action="capture" class="primary">Capturar</button>
                    </footer>
                </div>
            </div>`;
    this.canvas = this._shadow.querySelector("canvas");
    const video = this._shadow.querySelector("video");
    const md = globalThis.navigator?.mediaDevices;
    if (md && video) {
      md.getUserMedia({ video: { facingMode: facing } }).then((stream) => {
        this._stream = stream;
        video.srcObject = stream;
      }).catch(() => this.close(null));
    }
    this._shadow.querySelector(".xone-buttons")?.addEventListener("click", (ev) => {
      const t = ev.target;
      const a = t.getAttribute("data-action");
      if (a === "cancel") {
        this.stopStream();
        return this.close(null);
      }
      if (a === "capture" && video && this.canvas) {
        const ctx = this.canvas.getContext("2d");
        if (ctx) ctx.drawImage(video, 0, 0, this.canvas.width, this.canvas.height);
        this.stopStream();
        this.close(this.canvas);
      }
    });
  }
  stopStream() {
    if (this._stream) {
      for (const t of this._stream.getTracks()) t.stop();
      this._stream = null;
    }
  }
  disconnectedCallback() {
    this.stopStream();
  }
};
function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function baseModalCss() {
  return `
        :host { all: initial; }
        .xone-overlay {
            position: fixed; inset: 0;
            background: rgba(0,0,0,0.4);
            display: flex; align-items: center; justify-content: center;
            z-index: 2147483600;
            font-family: system-ui, sans-serif;
        }
        .xone-card {
            background: #ffffff; color: #222222;
            border-radius: 8px;
            min-width: 280px; max-width: 90vw;
            padding: 16px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.25);
        }
        .xone-title { font-weight: bold; margin-bottom: 8px; font-size: 16px; }
        .xone-body { margin-bottom: 16px; font-size: 14px; white-space: pre-wrap; }
        .xone-buttons { display: flex; gap: 8px; justify-content: flex-end; }
        .xone-buttons button {
            border: 1px solid #cccccc; background: #f5f5f5; padding: 6px 14px;
            border-radius: 4px; cursor: pointer; font-size: 14px;
        }
        .xone-buttons button.primary { background: #2266dd; color: white; border-color: #2266dd; }
        .xone-input {
            display: block; width: 100%; box-sizing: border-box;
            padding: 6px 8px; font-size: 14px;
            border: 1px solid #cccccc; border-radius: 4px;
        }
    `;
}
function toastCss() {
  return `
        :host { all: initial; }
        .xone-toast {
            position: fixed; left: 50%; bottom: 40px;
            transform: translateX(-50%);
            background: rgba(0,0,0,0.85); color: #ffffff;
            padding: 10px 18px; border-radius: 20px;
            font-family: system-ui, sans-serif; font-size: 14px;
            z-index: 2147483600;
        }
    `;
}
function snackbarCss() {
  return `
        :host { all: initial; }
        .xone-snackbar {
            position: fixed; left: 16px; right: 16px; bottom: 16px;
            background: #323232; color: #ffffff;
            border-radius: 4px; padding: 10px 16px;
            display: flex; align-items: center; justify-content: space-between;
            font-family: system-ui, sans-serif; font-size: 14px;
            z-index: 2147483600;
        }
        .xone-snackbar .action {
            background: transparent; color: #ff9933; border: none;
            font-weight: bold; cursor: pointer; padding: 0 0 0 16px;
        }
    `;
}
function waitCss() {
  return `
        .xone-wait { text-align: center; min-width: 240px; }
        .spinner {
            width: 32px; height: 32px;
            border: 4px solid #eeeeee; border-top-color: #2266dd;
            border-radius: 50%;
            margin: 0 auto 12px;
            animation: xone-spin 0.8s linear infinite;
        }
        @keyframes xone-spin { to { transform: rotate(360deg); } }
        .bar { width: 100%; }
    `;
}
function signatureCss() {
  return `
        .sig-canvas { display: block; touch-action: none; }
    `;
}
function cameraCss() {
  return `
        .cam-video, .cam-canvas { display: block; max-width: 100%; }
    `;
}
var XoneConfirmElement = class extends XoneMsgBoxElement {
};
var ELEMENT_MAP = {
  "xone-msgbox": XoneMsgBoxElement,
  "xone-confirm": XoneConfirmElement,
  "xone-prompt": XonePromptElement,
  "xone-toast": XoneToastElement,
  "xone-snackbar": XoneSnackbarElement,
  "xone-wait": XoneWaitElement,
  "xone-datepicker": XoneDatePickerElement,
  "xone-timepicker": XoneTimePickerElement,
  "xone-signature": XoneSignatureElement,
  "xone-camera": XoneCameraElement
};
function registerWebComponents() {
  if (typeof customElements === "undefined") return;
  for (const tag of TAGS) {
    if (!customElements.get(tag)) {
      try {
        customElements.define(tag, ELEMENT_MAP[tag]);
      } catch {
      }
    }
  }
}

// src/dialogs/portal.ts
var PORTAL_ID = "xone-ui-portal";
function getOrCreatePortal(provided) {
  if (provided) return provided;
  if (typeof document === "undefined") return null;
  let portal = document.getElementById(PORTAL_ID);
  if (!portal) {
    portal = document.createElement("div");
    portal.id = PORTAL_ID;
    portal.setAttribute("aria-live", "polite");
    document.body.appendChild(portal);
  }
  return portal;
}
function mount(portal, tag, attrs = {}) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    el.setAttribute(k, v);
  }
  portal.appendChild(el);
  return el;
}

// src/dialogs/MsgBox.ts
async function msgBox(deps, message, title = "", buttons = 0, icon = "info") {
  const override = deps.stateBag.setMessageBoxResult;
  if (override !== null) {
    deps.stateBag.setMessageBoxResult = null;
    return override;
  }
  const portal = getOrCreatePortal(deps.portal);
  if (!portal) {
    throw new XoneUiError("XONE.UI.PORTAL_NOT_AVAILABLE", "No hay portal DOM disponible.");
  }
  const iconName = typeof icon === "number" ? mapNumericIcon(icon) : icon;
  const el = mount(portal, "xone-msgbox", {
    message: String(message ?? ""),
    title: String(title ?? ""),
    icon: iconName,
    buttons: String(buttons)
  });
  return waitForResult(el).then((result) => result ?? 1);
}
async function msgBoxWithSound(deps, message, title = "", buttons = 0, icon = "info", sound = "default") {
  if (deps.playSound) {
    try {
      await deps.playSound(sound);
    } catch {
    }
  }
  return msgBox(deps, message, title, buttons, icon);
}
async function confirm(deps, message, title = "") {
  const r = await msgBox(deps, message, title, 4, "question");
  return r === 6;
}
async function prompt(deps, message, defaultValue = "", title = "") {
  const portal = getOrCreatePortal(deps.portal);
  if (!portal) return null;
  const el = mount(portal, "xone-prompt", {
    message: String(message ?? ""),
    title: String(title ?? ""),
    default: String(defaultValue ?? "")
  });
  return waitForResult(el);
}
function mapNumericIcon(n) {
  switch (n) {
    case 1:
      return "warning";
    case 2:
      return "error";
    case 3:
      return "question";
    default:
      return "info";
  }
}
function waitForResult(el) {
  return new Promise((resolve) => {
    const handler = (ev) => {
      const detail = ev.detail;
      resolve(detail.result);
    };
    el.addEventListener("xone-result", handler, { once: true });
    const dialog = el;
    if (dialog.bindPromise) {
      dialog.bindPromise(resolve, () => resolve(null));
    }
  });
}

// src/dialogs/Toast.ts
function showToast(portal, message, duration = 3e3) {
  const p = getOrCreatePortal(portal);
  if (!p) return;
  mount(p, "xone-toast", {
    message: String(message ?? ""),
    duration: String(duration)
  });
}

// src/dialogs/Snackbar.ts
var _current = null;
function showSnackbar(portal, message, actionLabel, actionCallback, duration = 4e3) {
  const p = getOrCreatePortal(portal);
  if (!p) return;
  hideSnackbar();
  const attrs = {
    message: String(message ?? ""),
    duration: String(duration)
  };
  if (actionLabel) attrs["action-label"] = actionLabel;
  const el = mount(p, "xone-snackbar", attrs);
  if (actionCallback) {
    el.actionCallback = actionCallback;
  }
  _current = el;
}
function hideSnackbar() {
  if (_current && _current.parentNode) {
    const dismiss = _current.dismiss;
    if (typeof dismiss === "function") dismiss.call(_current);
    else _current.parentNode.removeChild(_current);
  }
  _current = null;
}

// src/dialogs/WaitDialog.ts
function createWaitDialogService() {
  const state = { el: null, resolver: null };
  function show(portal, message, maxValue) {
    const p = getOrCreatePortal(portal);
    if (!p) return Promise.resolve();
    if (state.el && state.resolver) {
      setText(message ?? "Procesando...");
      if (maxValue !== void 0) setMax(maxValue);
      return new Promise((res) => {
        state.resolver = res;
      });
    }
    const attrs = {};
    if (message) attrs["message"] = message;
    if (maxValue !== void 0) attrs["max-value"] = String(maxValue);
    state.el = mount(p, "xone-wait", attrs);
    return new Promise((res) => {
      state.resolver = res;
    });
  }
  function hide() {
    if (state.el && state.el.parentNode) {
      state.el.parentNode.removeChild(state.el);
    }
    const r = state.resolver;
    state.el = null;
    state.resolver = null;
    if (r) r();
  }
  function setText(message) {
    if (state.el) {
      const ext = state.el;
      ext.setMessage?.(message);
    }
  }
  function setMax(maxValue) {
    if (state.el) {
      const ext = state.el;
      ext.setMaxValue?.(maxValue);
    }
  }
  function update(value, message) {
    if (state.el) {
      const ext = state.el;
      ext.update?.(value, message);
    }
  }
  return { show, hide, setText, setMax, update };
}

// src/dialogs/Pickers.ts
function pick(portal, tag, initial) {
  const p = getOrCreatePortal(portal);
  if (!p) return Promise.resolve(null);
  const attrs = {};
  if (initial) attrs["initial"] = initial.toISOString();
  const el = mount(p, tag, attrs);
  return new Promise((resolve) => {
    const handler = (ev) => {
      const detail = ev.detail;
      resolve(detail.result);
    };
    el.addEventListener("xone-result", handler, { once: true });
  });
}
function showDatePicker(portal, _propName, initialValue) {
  return pick(portal, "xone-datepicker", initialValue);
}
function showTimePicker(portal, _propName, initialValue) {
  return pick(portal, "xone-timepicker", initialValue);
}

// src/navigation/viewStack.ts
function createFallbackRouter() {
  const stack = [];
  return {
    openMenu(collection, params) {
      stack.length = 0;
      stack.push({
        id: `view-${stack.length + 1}-${collection}`,
        collection,
        target: null,
        params: { ...params ?? {} }
      });
    },
    openEditView(target, params) {
      const collection = typeof target === "string" ? target : "detail";
      stack.push({
        id: `view-${stack.length + 1}-${collection}`,
        collection,
        target,
        params: { ...params ?? {} }
      });
    },
    returnToMainMenu() {
      if (stack.length > 1) stack.length = 1;
    },
    getStack() {
      return stack.slice();
    },
    getCurrent() {
      return stack.length > 0 ? stack[stack.length - 1] ?? null : null;
    }
  };
}

// src/gps/geolocation.ts
function createGpsState() {
  return { last: null, watchId: null, running: false };
}
function toLocation(pos) {
  return {
    latitude: pos.coords.latitude,
    longitude: pos.coords.longitude,
    altitude: pos.coords.altitude,
    accuracy: pos.coords.accuracy,
    speed: pos.coords.speed,
    heading: pos.coords.heading,
    timestamp: pos.timestamp,
    provider: "web"
  };
}
async function startGps(state, options) {
  const geo = globalThis.navigator?.geolocation;
  if (!geo) {
    throw new XoneUiError("XONE.UI.GPS_UNAVAILABLE", "navigator.geolocation no disponible.");
  }
  const opts = {
    enableHighAccuracy: options?.enableHighAccuracy ?? true,
    timeout: options?.timeout ?? 1e4,
    maximumAge: options?.maximumAge ?? 0
  };
  return new Promise((resolve, reject) => {
    const success = (pos) => {
      const loc = toLocation(pos);
      state.last = loc;
      resolve(loc);
    };
    const failure = (err) => {
      reject(
        new XoneUiError("XONE.UI.GPS_UNAVAILABLE", err.message, {
          code: err.code
        })
      );
    };
    if (options?.continuous) {
      const id = geo.watchPosition(
        (pos) => {
          state.last = toLocation(pos);
          if (!state.running) {
            state.running = true;
            resolve(state.last);
          }
        },
        failure,
        opts
      );
      state.watchId = id;
    } else {
      geo.getCurrentPosition(success, failure, opts);
    }
  });
}
function stopGps(state) {
  const geo = globalThis.navigator?.geolocation;
  if (geo && state.watchId !== null) {
    geo.clearWatch(state.watchId);
  }
  state.watchId = null;
  state.running = false;
  return 0;
}
function getLastKnownLocation(state) {
  return state.last;
}
async function checkGPSStatus() {
  const nav = globalThis.navigator;
  if (!nav?.permissions) return !!nav?.geolocation;
  try {
    const status = await nav.permissions.query({ name: "geolocation" });
    return status.state !== "denied";
  } catch {
    return !!nav?.geolocation;
  }
}
async function askUserForGPSPermission(state, callback) {
  try {
    await startGps(state);
    callback?.(true);
    return true;
  } catch {
    callback?.(false);
    return false;
  }
}

// src/camera/captureImage.ts
async function captureImage(portal, storage, options) {
  const p = getOrCreatePortal(portal);
  if (!p) {
    throw new XoneUiError("XONE.UI.CAMERA_UNAVAILABLE", "No hay DOM disponible.");
  }
  const attrs = {
    width: String(options?.width ?? 640),
    height: String(options?.height ?? 480),
    facing: options?.facing ?? "environment"
  };
  const el = mount(p, "xone-camera", attrs);
  const canvas = await new Promise((resolve) => {
    el.addEventListener(
      "xone-result",
      (ev) => {
        const detail = ev.detail;
        resolve(detail.result);
      },
      { once: true }
    );
  });
  if (!canvas) {
    throw new XoneUiError("XONE.UI.CANCELED", "Captura cancelada.");
  }
  const mime = options?.mimeType ?? "image/jpeg";
  const quality = options?.quality ?? 0.9;
  const dataUrl = canvas.toDataURL(mime, quality);
  if (storage?.saveBinaryFile) {
    const path = options?.outputPath ?? `files/captures/${Date.now()}.${mime.split("/")[1]}`;
    const blob = dataUrlToBlob(dataUrl);
    return storage.saveBinaryFile(path, blob);
  }
  return dataUrl;
}
function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(",");
  const m = meta?.match(/data:(.*);base64/);
  const mime = m?.[1] ?? "application/octet-stream";
  const binary = atob(b64 ?? "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// src/signature/signaturePad.ts
async function startSignature(portal, storage, options) {
  const p = getOrCreatePortal(portal);
  if (!p) throw new XoneUiError("XONE.UI.SIGNATURE_UNAVAILABLE", "No hay DOM disponible.");
  const attrs = {
    width: String(options?.width ?? 400),
    height: String(options?.height ?? 200),
    title: options?.title ?? "Firma"
  };
  if (options?.penColor) attrs["pen-color"] = options.penColor;
  if (options?.backgroundColor) attrs["bg-color"] = options.backgroundColor;
  const el = mount(p, "xone-signature", attrs);
  const canvas = await new Promise((resolve) => {
    el.addEventListener(
      "xone-result",
      (ev) => {
        const detail = ev.detail;
        resolve(detail.result);
      },
      { once: true }
    );
  });
  if (!canvas) throw new XoneUiError("XONE.UI.CANCELED", "Firma cancelada.");
  const dataUrl = canvas.toDataURL("image/png");
  if (storage?.saveBinaryFile) {
    const path = options?.outputPath ?? `files/signatures/${Date.now()}.png`;
    return storage.saveBinaryFile(path, dataUrlToBlob2(dataUrl));
  }
  return dataUrl;
}
async function signDataObject(portal, storage, backend, target, options) {
  const path = await startSignature(portal, storage, options);
  if (backend?.signObject) {
    const hash = simpleHash(`${path}::${JSON.stringify(target ?? null)}`);
    try {
      return await backend.signObject({ hash, fields: target });
    } catch {
    }
  }
  return path;
}
async function saveDrawing(canvas, storage, filePath) {
  const dataUrl = canvas.toDataURL("image/png");
  if (storage?.saveBinaryFile) {
    const path = filePath ?? `files/drawings/${Date.now()}.png`;
    return storage.saveBinaryFile(path, dataUrlToBlob2(dataUrl));
  }
  return dataUrl;
}
async function mergeImagesLeftToRight(imagePath1, imagePath2, output) {
  if (typeof document === "undefined") {
    throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "Sin DOM.");
  }
  const img1 = await loadImage(imagePath1);
  const img2 = await loadImage(imagePath2);
  const canvas = document.createElement("canvas");
  canvas.width = img1.width + img2.width;
  canvas.height = Math.max(img1.height, img2.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "No 2D context.");
  ctx.drawImage(img1, 0, 0);
  ctx.drawImage(img2, img1.width, 0);
  return output ?? canvas.toDataURL("image/png");
}
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = src;
  });
}
function dataUrlToBlob2(dataUrl) {
  const [meta, b64] = dataUrl.split(",");
  const m = meta?.match(/data:(.*);base64/);
  const mime = m?.[1] ?? "application/octet-stream";
  const binary = atob(b64 ?? "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
function simpleHash(input) {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = h * 31 + input.charCodeAt(i) | 0;
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

// src/media/audio.ts
function createAudioState() {
  return {
    currentAudio: null,
    currentUtterance: null,
    currentRecorder: null,
    recordedChunks: [],
    volume: 1
  };
}
async function playSound(state, resource, loop = false) {
  if (typeof Audio === "undefined") {
    throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "Audio no disponible.");
  }
  const a = new Audio(resource);
  a.loop = loop;
  a.volume = state.volume;
  state.currentAudio = a;
  await new Promise((resolve, reject) => {
    a.addEventListener("canplaythrough", () => resolve(), { once: true });
    a.addEventListener("error", () => reject(new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "Error de audio.")), {
      once: true
    });
    a.play().catch(reject);
  });
}
async function playSoundAndVibrate(state, resource, durationMs) {
  vibrate(durationMs);
  await playSound(state, resource);
}
function stopPlaySound(state) {
  if (state.currentAudio) {
    state.currentAudio.pause();
    state.currentAudio.currentTime = 0;
    state.currentAudio = null;
  }
}
function setPlaySoundVolume(state, volume) {
  state.volume = Math.max(0, Math.min(1, volume));
  if (state.currentAudio) state.currentAudio.volume = state.volume;
}
function getSoundVolume(state) {
  return state.volume;
}
function getMaxSoundVolume() {
  return 1;
}
function vibrate(duration, pattern) {
  const nav = globalThis.navigator;
  if (!nav?.vibrate) return;
  if (pattern) nav.vibrate(pattern.slice());
  else nav.vibrate(duration);
}
function stopPlaySoundAndVibrate(state) {
  stopPlaySound(state);
  const nav = globalThis.navigator;
  nav?.vibrate?.(0);
}
async function speak(state, text, language, callback) {
  const ss = globalThis.speechSynthesis;
  if (!ss) throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "speechSynthesis no disponible.");
  const utt = new SpeechSynthesisUtterance(text);
  if (language) utt.lang = language;
  state.currentUtterance = utt;
  await new Promise((resolve) => {
    utt.onend = () => {
      state.currentUtterance = null;
      callback?.();
      resolve();
    };
    utt.onerror = () => {
      state.currentUtterance = null;
      resolve();
    };
    ss.speak(utt);
  });
}
function isSpeaking(state) {
  return state.currentUtterance !== null;
}
function stopSpeaking(state) {
  const ss = globalThis.speechSynthesis;
  ss?.cancel();
  state.currentUtterance = null;
}
function getAvailableSpeakLanguages() {
  const ss = globalThis.speechSynthesis;
  if (!ss) return [];
  return ss.getVoices().map((v) => v.lang);
}
async function recognizeSpeech(_language, _prompt) {
  const SR = globalThis;
  const Ctor = SR.SpeechRecognition ?? SR.webkitSpeechRecognition;
  if (!Ctor) throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "SpeechRecognition no disponible.");
  return new Promise((resolve, reject) => {
    const r = new Ctor();
    if (_language) r.lang = _language;
    r.continuous = false;
    r.interimResults = false;
    r.onresult = (ev) => {
      const result = ev.results?.[0]?.[0]?.transcript ?? "";
      resolve(result);
    };
    r.onerror = (ev) => reject(new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", String(ev?.error ?? "stt error")));
    r.start();
  });
}
async function startAudioRecord(state) {
  const md = globalThis.navigator?.mediaDevices;
  if (!md) throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "mediaDevices no disponible.");
  const stream = await md.getUserMedia({ audio: true });
  state.recordedChunks = [];
  const mr = new MediaRecorder(stream);
  mr.ondataavailable = (ev) => {
    if (ev.data.size > 0) state.recordedChunks.push(ev.data);
  };
  state.currentRecorder = mr;
  mr.start();
}
async function stopAudioRecord(state) {
  if (!state.currentRecorder) return null;
  return new Promise((resolve) => {
    const mr = state.currentRecorder;
    mr.onstop = () => {
      const blob = new Blob(state.recordedChunks, { type: "audio/webm" });
      state.currentRecorder = null;
      state.recordedChunks = [];
      resolve(blob);
    };
    mr.stop();
  });
}

// src/notifications/webNotifications.ts
var INSTANCES = /* @__PURE__ */ new Map();
async function showNotification(id, title, text, options) {
  const NotifCtor = globalThis.Notification;
  if (!NotifCtor) return;
  if (NotifCtor.permission === "default") {
    try {
      await NotifCtor.requestPermission();
    } catch {
      return;
    }
  }
  if (NotifCtor.permission !== "granted") return;
  const n = new NotifCtor(title, {
    body: text,
    icon: options?.icon,
    badge: options?.badge,
    tag: options?.tag ?? id,
    silent: options?.silent,
    data: options?.data
  });
  INSTANCES.set(id, n);
}
function dismissNotification(id) {
  const n = INSTANCES.get(id);
  if (n) {
    n.close();
    INSTANCES.delete(id);
  }
}
function areNotificationsBlocked() {
  const NotifCtor = globalThis.Notification;
  if (!NotifCtor) return true;
  return NotifCtor.permission === "denied";
}

// src/files/share.ts
async function shareData(options) {
  const nav = globalThis.navigator;
  if (!nav?.share) {
    throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "navigator.share no disponible.");
  }
  const data = {};
  if (options.title) data.title = options.title;
  if (options.text) data.text = options.text;
  if (options.url) data.url = options.url;
  if (options.files) data.files = options.files.slice();
  await nav.share(data);
}
async function openFile(path, mimeType) {
  if (typeof document === "undefined") {
    throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "No DOM.");
  }
  const w = window.open(path, "_blank", "noopener,noreferrer");
  if (!w && mimeType) {
    const a = document.createElement("a");
    a.href = path;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
  }
}
async function openUrl(url) {
  if (typeof window === "undefined") {
    throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "No window.");
  }
  window.open(url, "_blank", "noopener,noreferrer");
}
function canOpen(_uri) {
  return typeof window !== "undefined";
}
async function pickFile(_options) {
  if (typeof document === "undefined") return null;
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    if (_options?.accept) input.accept = _options.accept;
    if (_options?.multiple) input.multiple = true;
    input.style.display = "none";
    document.body.appendChild(input);
    input.addEventListener(
      "change",
      () => {
        const file = input.files?.[0];
        document.body.removeChild(input);
        if (!file) return resolve(null);
        const path = URL.createObjectURL(file);
        resolve({
          path,
          name: file.name,
          mimeType: file.type,
          size: file.size
        });
      },
      { once: true }
    );
    input.click();
  });
}

// src/print/pdfPrint.ts
function createPrintState() {
  return { current: null, lastPrinter: null };
}
async function startPrint(state, printerName, _options) {
  state.current = {
    printerName: printerName ?? state.lastPrinter ?? "default",
    entries: []
  };
}
async function useLastPrinter(state, fallback) {
  const name = state.lastPrinter ?? fallback ?? "default";
  state.current = { printerName: name, entries: [] };
  return name;
}
function requireSession(state) {
  if (!state.current) {
    throw new XoneUiError("XONE.UI.PRINT_NOT_STARTED", "startPrint() no fue invocado.");
  }
  return state.current;
}
async function printLine(state, text, opts) {
  const s = requireSession(state);
  s.entries.push({ kind: "text", text: String(text ?? ""), opts: { ...opts ?? {} } });
}
async function printText(state, text, opts) {
  return printLine(state, text, opts);
}
async function lineFeed(state, n = 1) {
  const s = requireSession(state);
  s.entries.push({ kind: "feed", n });
}
async function printBarcode(state, code, type = "CODE128", opts) {
  const s = requireSession(state);
  s.entries.push({ kind: "barcode", code: String(code ?? ""), type, opts: { ...opts ?? {} } });
}
async function printImage(state, path, opts) {
  const s = requireSession(state);
  s.entries.push({ kind: "image", path, opts: { ...opts ?? {} } });
}
async function printPDF(state, path) {
  if (typeof window === "undefined") {
    throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "No window.");
  }
  window.open(path, "_blank", "noopener,noreferrer");
  state.current = null;
}
async function printCommand(state, bytes) {
  const s = requireSession(state);
  s.entries.push({ kind: "command", bytes });
}
async function endPrint(state) {
  const s = state.current;
  state.current = null;
  if (!s) return;
  state.lastPrinter = s.printerName;
  if (typeof document === "undefined") return;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  if (!doc) return;
  const html = renderEntriesToHtml(s.entries);
  doc.open();
  doc.write(html);
  doc.close();
  try {
    iframe.contentWindow?.print();
  } finally {
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1e3);
  }
}
function renderEntriesToHtml(entries) {
  const body = entries.map((e) => {
    if (e.kind === "text") {
      const align = e.opts.align ?? "left";
      const bold = e.opts.bold ? "font-weight:bold;" : "";
      const size = e.opts.fontSize ? `font-size:${e.opts.fontSize}p;` : "";
      return `<div style="text-align:${align};${bold}${size}white-space:pre">${escapeHtml2(e.text)}</div>`;
    }
    if (e.kind === "feed") return "<br/>".repeat(Math.max(1, e.n));
    if (e.kind === "barcode")
      return `<div style="font-family:'Libre Barcode 128',monospace;font-size:48p">${escapeHtml2(e.code)}</div>`;
    if (e.kind === "image") return `<img src="${escapeAttr(e.path)}" />`;
    return "";
  }).join("");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ticket</title></head><body>${body}</body></html>`;
}
function escapeHtml2(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function escapeAttr(s) {
  return s.replace(/"/g, "&quot;");
}

// src/refresh/groupState.ts
function createGroupStore(renderer) {
  const map = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Set();
  const set = (name, visible, locked) => {
    const current = map.get(name);
    const next = {
      name,
      visible,
      locked: locked ?? current?.locked ?? false
    };
    map.set(name, next);
    for (const l of listeners) l(next);
    renderer?.setGroupVisible?.(name, visible);
    if (locked !== void 0) renderer?.setGroupLocked?.(name, locked);
  };
  const get = (name) => map.get(name) ?? { name, visible: true, locked: false };
  return {
    set,
    get,
    visible: (n) => get(n).visible,
    locked: (n) => get(n).locked,
    snapshot: () => new Map(map),
    onChange: (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    }
  };
}

// src/inactivity/inactivityTimer.ts
function createInactivityState() {
  return {
    handle: null,
    lastActivity: Date.now(),
    seconds: 0,
    callback: null,
    listeners: []
  };
}
function setInactivityTimer(state, seconds, callback) {
  removeInactivityTimer(state);
  state.seconds = seconds;
  state.callback = callback;
  state.lastActivity = Date.now();
  schedule(state);
  if (typeof window !== "undefined") {
    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    for (const ev of events) {
      const fn = () => {
        state.lastActivity = Date.now();
        schedule(state);
      };
      window.addEventListener(ev, fn, { passive: true });
      state.listeners.push({ target: window, event: ev, fn });
    }
  }
}
function removeInactivityTimer(state) {
  if (state.handle) clearTimeout(state.handle);
  state.handle = null;
  state.callback = null;
  state.seconds = 0;
  for (const l of state.listeners) {
    l.target.removeEventListener(l.event, l.fn);
  }
  state.listeners = [];
}
function getInactivityTime(state) {
  return Math.floor((Date.now() - state.lastActivity) / 1e3);
}
function schedule(state) {
  if (state.handle) clearTimeout(state.handle);
  if (state.seconds <= 0 || !state.callback) return;
  state.handle = setTimeout(() => {
    const cb = state.callback;
    if (cb) {
      try {
        void cb();
      } catch {
      }
    }
  }, state.seconds * 1e3);
}
function sleep(ms) {
  return new Promise((res) => setTimeout(res, ms));
}
function executeActionAfterDelay(fn, seconds) {
  return new Promise((res, rej) => {
    setTimeout(async () => {
      try {
        res(await fn());
      } catch (err) {
        rej(err);
      }
    }, seconds * 1e3);
  });
}

// src/scanner/scanner.ts
async function startScanner(options) {
  const gn = globalThis;
  const Det = gn.BarcodeDetector;
  if (!Det) {
    throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "BarcodeDetector no disponible.");
  }
  const md = globalThis.navigator?.mediaDevices;
  if (!md) throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "mediaDevices no disponible.");
  const stream = await md.getUserMedia({ video: { facingMode: options?.facing ?? "environment" } });
  const init = {};
  if (options?.formats) init.formats = options.formats.slice();
  const det = new Det(init);
  const video = document.createElement("video");
  video.srcObject = stream;
  video.setAttribute("playsinline", "true");
  await video.play();
  try {
    return await new Promise((resolve, reject) => {
      const tick = async () => {
        try {
          const res = await det.detect(video);
          if (res.length > 0 && res[0]) {
            resolve(res[0].rawValue);
            return;
          }
        } catch (err) {
          reject(err);
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  } finally {
    for (const t of stream.getTracks()) t.stop();
  }
}
async function scanDocument(_options) {
  throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "scanDocument no soportado en web (stub).");
}
async function recognizeText(_options) {
  throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "recognizeText requiere lazy load de tesseract.js.");
}

// src/stubs/system.ts
function startWifi() {
}
function stopWifi() {
}
function isWifiEnabled() {
  const nav = globalThis.navigator;
  return !!nav?.onLine;
}
function isWifiConnected() {
  return isWifiEnabled();
}
function startKioskMode() {
}
function stopKioskMode() {
}
function isInBackground() {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}
function isInForeground() {
  return !isInBackground();
}
function moveToBackground() {
  return Promise.resolve();
}
function returnToForeground() {
  return Promise.resolve();
}
function isDeviceLocked() {
  return false;
}
function isSuperuserAvailable() {
  return false;
}
function isApplicationInstalled(_pkg) {
  return false;
}
async function launchApplication(_pkg, _args) {
  throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "launchApplication no soportado en web.");
}
async function uninstallApplication(_pkg) {
  throw new XoneUiError("XONE.UI.UNSUPPORTED_FEATURE", "uninstallApplication no soportado en web.");
}
function garbageCollect() {
}
function dumpMemory(_path) {
}
function showSoftwareKeyboard() {
}
function hideSoftwareKeyboard() {
  if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}
function showKeyboardOptions() {
}
async function startForegroundService(_options) {
}
async function stopForegroundService(_options) {
}
async function restartApp() {
  if (typeof window !== "undefined") window.location.reload();
}
async function quitApp() {
  if (typeof window !== "undefined") window.close();
}
async function exit() {
  return quitApp();
}
async function createShortcut() {
}
async function deleteShortcut() {
}
async function requestAppReview() {
}
async function sendMail(to, subject, body, _attachments) {
  if (typeof window === "undefined") return;
  const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.open(url, "_self");
}
async function sendSMS(phone, message) {
  if (typeof window === "undefined") return;
  window.open(`sms:${encodeURIComponent(phone)}?body=${encodeURIComponent(message)}`, "_self");
}
async function makePhoneCall(phone) {
  if (typeof window === "undefined") return;
  window.open(`tel:${encodeURIComponent(phone)}`, "_self");
}
function canMakePhoneCall() {
  return typeof window !== "undefined";
}
function isOnCall() {
  return false;
}
async function startWhatsAppVideoCall(phone) {
  if (typeof window === "undefined") return;
  window.open(`https://wa.me/${encodeURIComponent(phone)}`, "_blank", "noopener,noreferrer");
}
async function addCalendarItem(title, beginDate, endDate, description, location) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const dtStart = formatIcsDate(beginDate);
  const dtEnd = formatIcsDate(endDate ?? new Date(beginDate.getTime() + 60 * 60 * 1e3));
  const ics = `BEGIN:VCALENDAR\r
VERSION:2.0\r
BEGIN:VEVENT\r
DTSTART:${dtStart}\r
DTEND:${dtEnd}\r
SUMMARY:${escapeIcs(title)}\r
` + (description ? `DESCRIPTION:${escapeIcs(description)}\r
` : "") + (location ? `LOCATION:${escapeIcs(location)}\r
` : "") + `END:VEVENT\r
END:VCALENDAR\r
`;
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/[^a-z0-9]/gi, "_")}.ics`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}
function formatIcsDate(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
}
function escapeIcs(s) {
  return s.replace(/[\r\n,;]/g, " ");
}
async function startReplica() {
}
async function stopReplica() {
}
async function startLive() {
}
async function openReplicaConsole() {
}
async function showConsoleReplica() {
}
async function injectJavascript(_propName, _jsCode) {
}

// src/factory/createUi.ts
var NOOP = () => {
};
var NOOP_LOGGER = {
  debug: NOOP,
  info: NOOP,
  warn: NOOP,
  error: NOOP
};
function buildActiveView(router) {
  const current = router.getCurrent();
  if (!current) return null;
  return {
    ...current,
    exit: async () => {
      if (typeof router.goBack === "function") {
        await router.goBack();
      } else {
        await router.returnToMainMenu();
      }
    }
  };
}
function createUi(config = {}) {
  registerWebComponents();
  const logger = config.logger ?? NOOP_LOGGER;
  const router = config.router ?? createFallbackRouter();
  const portal = getOrCreatePortal(config.dialogPortal ?? null);
  const xmlRenderer = config.xmlRenderer ?? null;
  const storage = config.storage ?? null;
  const backend = config.backendClient ?? null;
  let language = config.language ?? (typeof navigator !== "undefined" && typeof navigator.language === "string" ? navigator.language : "es-ES");
  let country = config.countryCode ?? extractCountry(language);
  const gpsState = createGpsState();
  const audioState = createAudioState();
  const printState = createPrintState();
  const waitDialog = createWaitDialogService();
  const inactivityState = createInactivityState();
  const groupStore = createGroupStore(xmlRenderer);
  const drawings = /* @__PURE__ */ new Map();
  const dialogStateBag = { setMessageBoxResult: null };
  const dialogDeps = {
    portal,
    playSound: (resource) => playSound(audioState, resource),
    stateBag: dialogStateBag
  };
  const r = xmlRenderer;
  const ui = {
    // 3.1
    openMenu: async (collection, params) => {
      logger.debug("openMenu", collection);
      await router.openMenu(collection, params);
      return 0;
    },
    openEditView: async (target, params) => {
      logger.debug("openEditView");
      await router.openEditView(target, params);
      return 0;
    },
    returnToMainMenu: async () => {
      await router.returnToMainMenu();
      return 0;
    },
    getView: (_target) => buildActiveView(router),
    getViewStack: () => router.getStack(),
    getWindow: (_target) => buildActiveView(router),
    getWindowStack: () => router.getStack(),
    getControl: (_propName, _target) => null,
    ensureVisible: NOOP,
    setSelection: NOOP,
    startDrag: NOOP,
    // 3.2
    msgBox: (m, t, b, i) => msgBox(dialogDeps, m, t, b, i),
    msgBoxWithSound: (m, t, b, i, s) => msgBoxWithSound(dialogDeps, m, t, b, i, s),
    confirm: (m, t) => confirm(dialogDeps, m, t),
    prompt: (m, d, t) => prompt(dialogDeps, m, d, t),
    showDatePicker: (propName, initial) => showDatePicker(portal, propName, initial),
    showTimePicker: (propName, initial) => showTimePicker(portal, propName, initial),
    showWaitDialog: (msg, max) => waitDialog.show(portal, msg, max),
    setMessageBoxResult: (result) => {
      dialogStateBag.setMessageBoxResult = result;
    },
    showToast: (m, d) => showToast(portal, m, d),
    showSnackbar: (m, label, cb, dur) => showSnackbar(portal, m, label, cb, dur),
    hideSnackbar,
    setWaitDialogText: (m) => waitDialog.setText(m),
    setMaxWaitDialog: (v) => waitDialog.setMax(v),
    updateWaitDialog: (v, m) => waitDialog.update(v, m),
    hideWaitDialog: () => waitDialog.hide(),
    // 3.3
    showNotification: async (id, title, text, options) => showNotification(id, title, text, options),
    showNotificationChannelSettings: async () => {
    },
    showNotificationSettings: async () => {
    },
    dismissNotification,
    setNotificationLed: NOOP,
    areNotificationsBlocked: () => areNotificationsBlocked(),
    // 3.4
    playSound: (resource, loop) => playSound(audioState, resource, loop),
    playSoundAndVibrate: (r1, d) => playSoundAndVibrate(audioState, r1, d),
    speak: (text, lang, cb) => speak(audioState, text, lang, cb),
    recognizeSpeech: (lang, p) => recognizeSpeech(lang, p),
    startAudioRecord: () => startAudioRecord(audioState),
    stopAudioRecord: () => stopAudioRecord(audioState),
    stopPlaySound: () => stopPlaySound(audioState),
    playSoundVolumen: (v) => setPlaySoundVolume(audioState, v),
    setPlaySoundVolume: (v) => setPlaySoundVolume(audioState, v),
    getSoundVolumen: () => getSoundVolume(audioState),
    getSoundVolume: () => getSoundVolume(audioState),
    getMaxSoundVolumen: () => getMaxSoundVolume(),
    getMaxSoundVolume: () => getMaxSoundVolume(),
    vibrate: (d, p) => vibrate(d, p),
    stopPlaySoundAndVibrate: () => stopPlaySoundAndVibrate(audioState),
    isSpeaking: () => isSpeaking(audioState),
    stopSpeaking: () => stopSpeaking(audioState),
    getAvailableSpeakLanguages: () => getAvailableSpeakLanguages(),
    stopRecognizeSpeech: NOOP,
    destroyRecognizeSpeech: NOOP,
    // 3.5
    startGps: (opts) => startGps(gpsState, opts),
    stopGps: () => stopGps(gpsState),
    getLastKnownLocation: () => getLastKnownLocation(gpsState),
    getLastKnownLocationGoogle: () => getLastKnownLocation(gpsState),
    getLastKnownLocationGps: () => getLastKnownLocation(gpsState),
    getLastKnownLocationFused: () => getLastKnownLocation(gpsState),
    checkGPSStatus: () => checkGPSStatus(),
    checkGpsStatus: () => checkGPSStatus(),
    askUserForGPSPermission: (cb) => askUserForGPSPermission(gpsState, cb),
    askUserForGpsPermission: (cb) => askUserForGPSPermission(gpsState, cb),
    drawMapRoute: async () => {
    },
    // 3.6
    startCamera: async () => {
    },
    captureImage: (opts) => captureImage(portal, storage, opts),
    startSignature: (opts) => startSignature(portal, storage, opts),
    signDataObject: (t, opts) => signDataObject(portal, storage, backend, t, opts),
    saveDrawing: async (propName, filePath) => {
      const canvas = drawings.get(propName);
      if (!canvas) {
        throw new XoneUiError("XONE.UI.INVALID_ARGUMENT", `No hay drawing en '${propName}'.`);
      }
      return saveDrawing(canvas, storage, filePath);
    },
    mergeImagesLeftToRight: (a, b, o) => mergeImagesLeftToRight(a, b, o),
    clearDrawing: (propName) => {
      const c = drawings.get(propName);
      if (c) {
        const ctx = c.getContext("2d");
        ctx?.clearRect(0, 0, c.width, c.height);
      }
    },
    hasDrawing: (propName) => drawings.has(propName),
    // 3.7
    openFile: (path, mime) => openFile(path, mime),
    openUrl: (url) => openUrl(url),
    sharedData: (o) => shareData(o),
    shareData: (o) => shareData(o),
    pickFile: (o) => pickFile(o),
    canOpen,
    // 3.8
    startPrint: (printer, opts) => startPrint(printState, printer, opts),
    endPrint: () => endPrint(printState),
    useLastPrinter: (fallback) => useLastPrinter(printState, fallback),
    print: (text, opts) => printText(printState, text, opts),
    printLine: (text, opts) => printLine(printState, text, opts),
    printCommand: (bytes) => printCommand(printState, bytes),
    lineFeed: (n) => lineFeed(printState, n),
    printBarcode: (code, type, opts) => printBarcode(printState, code, type, opts),
    printPDF: (path) => printPDF(printState, path),
    setFeedMode: NOOP,
    printBIDI: (text, opts) => printText(printState, text, opts),
    printImage: (path, opts) => printImage(printState, path, opts),
    // 3.9
    sendMail: (to, subject, body, attachments) => sendMail(to, subject, body, attachments),
    sendSMS: (p, m) => sendSMS(p, m),
    sendSms: (p, m) => sendSMS(p, m),
    sendSMSRetryIfNetworkIsDown: (p, m) => sendSMS(p, m),
    sendSmsRetryIfNetworkIsDown: (p, m) => sendSMS(p, m),
    makePhoneCall: (n) => makePhoneCall(n),
    startWhatsAppVideoCall: (p) => startWhatsAppVideoCall(p),
    canMakePhoneCall: () => canMakePhoneCall(),
    isOnCall: () => isOnCall(),
    // 3.10
    startWifi,
    stopWifi,
    isWifiEnabled,
    isWifiConnected,
    /**
     * `ui.isNetworkAvailable([checkInternetConnection])` (paridad XOne 1.2.0).
     * SPA web:
     *  - sin args -> sync, retorna `0` si `navigator.onLine===true`, sino `-1`.
     *  - `checkInternetConnection===true` -> async, hace un `fetch HEAD no-cors`
     *    al endpoint `config.networkProbeUrl` (default `'https://www.gstatic.com/generate_204'`)
     *    con timeout 5s. Retorna `0`/`-1`.
     */
    isNetworkAvailable: ((checkInternetConnection) => {
      const nav = globalThis.navigator;
      const onLineLocal = nav?.onLine !== false;
      if (checkInternetConnection !== true) {
        return onLineLocal ? 0 : -1;
      }
      return (async () => {
        if (!onLineLocal) return -1;
        const probeUrl = config.networkProbeUrl ?? "https://www.gstatic.com/generate_204";
        const fetchFn = globalThis.fetch;
        if (typeof fetchFn !== "function") {
          return onLineLocal ? 0 : -1;
        }
        const controller = typeof AbortController === "function" ? new AbortController() : void 0;
        const timer = controller ? setTimeout(() => controller.abort(), 5e3) : null;
        try {
          await fetchFn(probeUrl, {
            method: "HEAD",
            mode: "no-cors",
            cache: "no-store",
            ...controller ? { signal: controller.signal } : {}
          });
          return 0;
        } catch {
          return -1;
        } finally {
          if (timer) clearTimeout(timer);
        }
      })();
    }),
    startScanner: (opts) => startScanner(opts),
    restartApp: () => restartApp(),
    quitApp: () => quitApp(),
    exit: () => exit(),
    moveToBackground: () => moveToBackground(),
    returnToForeground: () => returnToForeground(),
    startReplica: () => startReplica(),
    stopReplica: () => stopReplica(),
    startLive: () => startLive(),
    openReplicaConsole: () => openReplicaConsole(),
    showConsoleReplica: () => showConsoleReplica(),
    launchApplication: (pkg, args) => launchApplication(pkg, args),
    launchApp: (pkg, args) => launchApplication(pkg, args),
    uninstallApplication: (pkg) => uninstallApplication(pkg),
    uninstallApp: (pkg) => uninstallApplication(pkg),
    requestAppReview: () => requestAppReview(),
    injectJavascript: (prop, code) => injectJavascript(prop, code),
    startKioskMode,
    stopKioskMode,
    isInBackground,
    isInForeground,
    isDeviceLocked,
    isSuperuserAvailable,
    isApplicationInstalled,
    isAppInstalled: isApplicationInstalled,
    showSoftwareKeyboard,
    hideSoftwareKeyboard,
    showKeyboardOptions,
    // 3.11
    sleep: (ms) => sleep(ms),
    addCalendarItem: (title, begin, end, desc, loc) => addCalendarItem(title, begin, end, desc, loc),
    executeActionAfterDelay: async (functionName, seconds, _args) => {
      const fn = typeof functionName === "function" ? functionName : () => {
      };
      return executeActionAfterDelay(fn, seconds);
    },
    setLanguage: (code) => {
      language = code;
      country = extractCountry(code);
    },
    getLanguage: () => language,
    getCountryCode: () => country,
    setInactivityTimer: (s, cb) => setInactivityTimer(inactivityState, s, cb),
    removeInactivityTimer: () => removeInactivityTimer(inactivityState),
    getInactivityTime: () => getInactivityTime(inactivityState),
    // 3.12
    refresh: async (target) => {
      if (r) await r.refresh(target);
    },
    refreshAll: async (target) => {
      if (r) await r.refreshAll(target);
    },
    refreshValue: async (propName, target) => {
      if (r) await r.refreshValue(propName, target);
    },
    refreshContentRow: async (propName, index) => {
      if (r) await r.refreshContentRow(propName, index);
    },
    refreshContentSelectedRow: async (propName) => {
      if (r) await r.refreshContentSelectedRow(propName);
    },
    showGroup: (name, _t) => groupStore.set(name, true),
    hideGroup: (name, _t) => groupStore.set(name, false),
    toggleGroup: (name, _t) => groupStore.set(name, !groupStore.visible(name)),
    setGroupVisible: (name, visible, _t) => groupStore.set(name, visible),
    lockGroup: (name, _t) => groupStore.set(name, groupStore.visible(name), true),
    unlockGroup: (name, _t) => groupStore.set(name, groupStore.visible(name), false),
    setGroupLocked: (name, locked, _t) => groupStore.set(name, groupStore.visible(name), locked),
    isGroupVisible: (name) => groupStore.visible(name),
    isGroupLocked: (name) => groupStore.locked(name),
    relayout: () => {
      r?.relayout();
    },
    setScaleFontSize: NOOP,
    setFontFactor: NOOP,
    // 3.13
    createShortcut: () => createShortcut(),
    deleteShortcut: () => deleteShortcut(),
    scanDocument: (opts) => scanDocument(opts),
    recognizeText: (opts) => recognizeText(opts),
    startForegroundService: (o) => startForegroundService(o),
    stopForegroundService: (o) => stopForegroundService(o),
    garbageCollect,
    dumpMemory,
    _groupStore: groupStore
  };
  return ui;
}
function extractCountry(language) {
  const parts = language.split(/[-_]/);
  return (parts[1] ?? parts[0] ?? "ES").toUpperCase();
}

// src/types/index.ts
var MSG_BOX_BUTTONS = {
  OK: 0,
  OK_CANCEL: 1,
  ABORT_RETRY_IGNORE: 2,
  YES_NO_CANCEL: 3,
  YES_NO: 4,
  RETRY_CANCEL: 5
};
var MSG_BOX_RESULT = {
  OK: 1,
  CANCEL: 2,
  ABORT: 3,
  RETRY: 4,
  IGNORE: 5,
  YES: 6,
  NO: 7
};
export {
  MSG_BOX_BUTTONS,
  MSG_BOX_RESULT,
  XoneCameraElement,
  XoneDatePickerElement,
  XoneMsgBoxElement,
  XonePromptElement,
  XoneSignatureElement,
  XoneSnackbarElement,
  XoneTimePickerElement,
  XoneToastElement,
  XoneUiCanceledError,
  XoneUiError,
  XoneWaitElement,
  createFallbackRouter,
  createGroupStore,
  createUi,
  createWaitDialogService,
  getOrCreatePortal,
  registerWebComponents,
  xoneColorToCss
};
//# sourceMappingURL=index.js.map